import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

import { NgxButtonComponent } from '@digipay/ngx-button';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { UiFormFieldBuilderModule } from '@digipay/ui-form-field-builder';
import { WealthNavigationService } from '@client-monorepo/wealth/navigation';
import {
  CAMPAIGN_AGREEMENT_ROUTE,
  CAMPAIGN_OTP_ROUTE,
  CAMPAIGN_SHAHKAR_ERROR_ROUTE,
  COLLATERAL_ROUTE,
  CONFIRM_COLLATERAL_INFO_ROUTE,
  HOME_ROUTE,
  SEJAM_ERROR_ROUTE,
  TREASURE_HUNT_REGISTRATION_SUCCESSFUL_ROUTE,
  TREASURE_HUNT_START_ROUTE,
} from '../../../../data-access/constants/app-routes';
import { UserInfoModel } from '../../../user-profile/models/user-info.model';
import { FormErrorStatus } from '../../../../data-access/models/form-error-status.model';
import { validateNationalId } from '../../../../components/utils/strings';
import { RouteStateService } from '@client-monorepo/common/utilities';
import { CoordinatorPage } from '../../../../data-access/enums/coordinator-page';
import { IProcessData } from '../../../collateral/models/process-data.interface';
import { CollateralService } from '../../../collateral/services/collateral.service';
import { ECollateralPage } from '../../../collateral/models/collatetral-page.enum';
import { NgxAppBarComponent } from '@digipay/ngx-app-bar';
import { HomeService } from '../../../home/services/home.service';

@Component({
  selector: 'wealth-applet-campaign-national-id',
  standalone: true,
  imports: [CommonModule, NgxButtonComponent, ReactiveFormsModule, UiFormFieldBuilderModule, NgxAppBarComponent],
  templateUrl: './campaign-national-id.component.html',
  styleUrl: './campaign-national-id.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CampaignNationalIdComponent implements OnInit {
  user = signal<UserInfoModel | undefined>(undefined);
  form: FormGroup;
  nationalIdNotMatchError = false;
  nationalIdErrorMessage = 'کد ملی نامعتبر است';
  showError = signal<FormErrorStatus>('hidden');
  errorMessage = '';
  isLoading = signal(false);
  isInputDisabled = signal(false);

  state = {
    phoneNumber: '',
    nextPage: '',
    campaignCode: '',
    role: '',
    symbol: '',
    coordinatorAction: '',
    nationalId: '',
    maxAmount: '',
    minAmount: '',
    data: null,
  };
  private routeState = inject(RouteStateService);
  private homeService = inject(HomeService);
  private collateralService = inject(CollateralService);
  private navigationService = inject(WealthNavigationService);
  private formBuilder = inject(FormBuilder);

  ngOnInit(): void {
    this.state = this.routeState.getAll();
    if (!this.state.phoneNumber) this.navigationService.navigate([HOME_ROUTE]);
    this.form = this.formBuilder.group({
      nationalId: ['', [this.nationalIdValidator, Validators.minLength(10)]],
    });
    this.form.controls['nationalId'].valueChanges.subscribe((res) => {
      if (!res) {
        this.nationalIdNotMatchError = false;
        this.showError.set('hidden');
      }
    });
  }

  onBackHandler() {
    if (this.state.coordinatorAction) {
      this.navigationService.navigate([COLLATERAL_ROUTE, this.state.symbol]);
    } else {
      this.navigationService.navigate([TREASURE_HUNT_START_ROUTE], {
        state: {
          campaignCode: this.state.campaignCode,
          phoneNumber: this.state.phoneNumber,
          role: this.state.role,
        },
      });
    }
  }

  nationalIdChanged(): void {
    this.nationalIdNotMatchError = false;
    this.showError.set('hidden');
  }

  async onNationalCodeSubmit() {
    if (this.state.coordinatorAction) {
      const data: IProcessData = {
        nationalId: this.form.controls['nationalId'].value,
      };
      this.collateralService.process(data, this.state.coordinatorAction).subscribe((res) => {
        if (res?.success) {
          if (res && res.result && res.result.action === 'error') {
            this.nationalIdErrorMessage = res.result.data.message;
            this.showError.set('show');
            return;
          } else {
            switch (res.result.data.pageName) {
              case ECollateralPage.page_global_shahkar_exception:
                this.navigationService.navigate([CAMPAIGN_SHAHKAR_ERROR_ROUTE], {
                  state: {
                    phoneNumber: this.state.phoneNumber,
                  },
                });
                break;
              case ECollateralPage.page_collateral_form_confirm:
                this.navigationService.navigate([CONFIRM_COLLATERAL_INFO_ROUTE], {
                  state: {
                    phoneNumber: this.state.phoneNumber,
                    nationalId: res.result.data.nationalId,
                    maxAmount: res.result.data.maxAmount,
                    minAmount: res.result.data.minAmount,
                    minUnits: res.result.data.minUnits,
                    maxUnits: res.result.data.maxUnits,
                    coordinatorAction: res.result.data.coordinatorAction,
                    symbol: this.state.symbol,
                    data: res.result.data,
                  },
                });

                break;

              case ECollateralPage.page_global_sejami_exception:
                this.navigationService.navigate([SEJAM_ERROR_ROUTE]);
                break;
            }
          }
        }
      });
    } else {
      this.homeService
        .getCampaignProcess(this.state.campaignCode, null, { nationalId: this.form.controls['nationalId'].value })
        .subscribe((res) => {
          if (res?.success) {
            if (res.result['action'] === 'error') {
              this.nationalIdErrorMessage = res.result['data']['message'];
              this.showError.set('show');
              return;
            }
            if (res.result.data.pageName === CoordinatorPage.PAGE_GLOBAL_SHAHKAR_EXCEPTION) {
              //TODO shahkar Exception
              this.navigationService.navigate([CAMPAIGN_SHAHKAR_ERROR_ROUTE], {
                state: {
                  campaignCode: this.state.campaignCode,
                  phoneNumber: this.state.phoneNumber,
                },
              });
            } else if (res.result.data.pageName === CoordinatorPage.PAGE_GLOBAL_SEJAMI_EXCEPTION) {
              this.navigationService.navigate([SEJAM_ERROR_ROUTE]);
            } else if (res.result.data.pageName === CoordinatorPage.PAGE_CAMPAIGN_PRIZE_WAITING) {
              this.navigationService.navigate([TREASURE_HUNT_REGISTRATION_SUCCESSFUL_ROUTE], {
                state: {
                  remainingDays: res.result.data.remainingDays,
                },
              });
            } else if (res.result.data.pageName === CoordinatorPage.PAGE_CAMPAIGN_OTP) {
              this.navigationService.navigate([CAMPAIGN_OTP_ROUTE], {
                state: {
                  campaignCode: this.state.campaignCode,
                  countDown: res.result.data.countdownInSeconds,
                },
              });
            } else if (res.result.data.pageName === CoordinatorPage.PAGE_GLOBAL_ETF_CONTRACT_REGISTRATION) {
              this.navigationService.navigate([CAMPAIGN_AGREEMENT_ROUTE], {
                state: {
                  campaignCode: this.state.campaignCode,
                  userInfo: res.result.data.customerProfile,
                  agreements: res.result.data.contractEtfAgreements,
                  phoneNumber: this.state.phoneNumber,
                },
              });
            }
          }
        });
    }
  }

  private nationalIdValidator(control: AbstractControl): {
    [s: string]: boolean;
  } {
    if (validateNationalId(control.value)) {
      return null;
    }
    return { invalidNotionalCode: true };
  }
}
